// FilterPanel.js - Context Filters Component for ABLE

import React from 'react';
import { Menu, Star, X, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import { ABLE_COLORS, CONTEXT_OPTIONS, STATUS_OPTIONS, FILTER_CONFIG } from '../constants';
import { getStatusLabel } from '../utils/taskHelpers';
import useLocalStorage from '../hooks/useLocalStorage';

const STORAGE_KEY = 'mabel-filters-expanded';

export default function FilterPanel({
  capacity,
  setCapacity,
  statusFilter,
  setStatusFilter,
  showStarredOnly,
  setShowStarredOnly,
  expandedFilter,
  setExpandedFilter,
  contextOptions,
  viewMode
}) {
  const [isExpanded, setIsExpanded] = useLocalStorage(STORAGE_KEY, false);
  
  const toggleCapacity = (type, value) => {
    setCapacity(prev => {
      const currentArray = prev[type];
      const isSelected = currentArray.includes(value);
      
      return {
        ...prev,
        [type]: isSelected 
          ? currentArray.filter(v => v !== value)
          : [...currentArray, value]
      };
    });
  };

  const countActiveFilters = () => {
    let count = 0;
    // Count capacity filters
    Object.values(capacity).forEach(arr => {
      count += arr.length;
    });
    // Count starred filter
    if (showStarredOnly) count += 1;
    // Count status filter (if not default)
    if (statusFilter !== 'active') count += 1;
    return count;
  };

  const hasActiveFilters = () => {
    return countActiveFilters() > 0;
  };

  const clearAllFilters = () => {
    setCapacity({
      priority: [],
      area: [],
      project: [],
      release: [],
      workflow: [],
      timeOfDay: [],
      physical: [],
      mental: [],
      social: []
    });
    setShowStarredOnly(false);
    setStatusFilter('active');
    setExpandedFilter(null);
  };

  return (
    <div style={{
      background: ABLE_COLORS.neutral,
      borderRadius: ABLE_COLORS.cardRadius,
      marginBottom: '20px',
      boxShadow: ABLE_COLORS.cardShadow,
      overflow: 'visible'
    }}>
      {/* Clickable header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <SlidersHorizontal size={20} color={ABLE_COLORS.primary} />
        <span style={{
          fontSize: '14px',
          fontWeight: '600',
          color: ABLE_COLORS.textPrimary
        }}>
          Context Filters
        </span>
        {isExpanded ? (
          <ChevronUp size={16} color={ABLE_COLORS.textMuted} />
        ) : (
          <ChevronDown size={16} color={ABLE_COLORS.textMuted} />
        )}
        {hasActiveFilters() && (
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            color: 'white',
            background: ABLE_COLORS.primary,
            borderRadius: '10px',
            padding: '2px 6px',
            marginLeft: 'auto'
          }}>
            {countActiveFilters()} active
          </span>
        )}
      </button>

      {/* Filters Section - only shown when expanded */}
      {isExpanded && (
      <div style={{ padding: '0 16px 16px 16px' }}>
        {hasActiveFilters() && (
          <button
            onClick={clearAllFilters}
            style={{
              padding: '6px 12px',
              background: ABLE_COLORS.warm,
              color: 'white',
              border: 'none',
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',
              marginBottom: '12px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <X size={12} />
            Clear All
          </button>
        )}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* STATUS FILTER (only in plan view) */}
          {viewMode === 'plan' && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setExpandedFilter(expandedFilter === 'status' ? null : 'status')}
                style={{
                  padding: '8px 12px',
                  background: statusFilter !== 'active' || expandedFilter === 'status' ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                  color: statusFilter !== 'active' || expandedFilter === 'status' ? 'white' : ABLE_COLORS.textSecondary,
                  border: `2px solid ${statusFilter !== 'active' || expandedFilter === 'status' ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                  fontSize: '13px',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <Menu size={14} />
                <span>{statusFilter !== 'active' ? getStatusLabel(statusFilter) : 'Status'}</span>
              </button>
              
              {expandedFilter === 'status' && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 'auto',
                  marginTop: '8px',
                  padding: '12px',
                  background: ABLE_COLORS.neutral,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  boxShadow: ABLE_COLORS.cardShadowHover,
                  border: `1px solid ${ABLE_COLORS.border}`,
                  zIndex: 100,
                  minWidth: '180px',
                  maxWidth: 'calc(100vw - 40px)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {STATUS_OPTIONS.map((filter) => {
                      const isSelected = statusFilter === filter;

                      return (
                        <button
                          key={filter}
                          onClick={() => {
                            setStatusFilter(filter);
                            setExpandedFilter(null);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                            color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                            border: `2px solid ${isSelected ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                            borderRadius: ABLE_COLORS.radiusSmall,
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.15s ease',
                            textAlign: 'left'
                          }}
                        >
                          {getStatusLabel(filter)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PROJECT FILTERS */}
          {FILTER_CONFIG.filter(f => ['priority', 'area', 'project'].includes(f.key)).map(({ key, icon: Icon, label }) => {
            const selectedCount = capacity[key].length;
            const isActive = selectedCount > 0;
            const displayText = selectedCount === 1 ? capacity[key][0] : selectedCount > 1 ? `${selectedCount}` : label;
            const isExpanded = expandedFilter === key;
            
            return (
              <div key={key} style={{ position: 'relative' }}>
                <button
                  onClick={() => setExpandedFilter(isExpanded ? null : key)}
                  style={{
                    padding: '8px 12px',
                    background: isActive || isExpanded ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                    color: isActive || isExpanded ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${isActive || isExpanded ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Icon size={14} />
                  <span>{displayText}</span>
                  {selectedCount > 1 && (
                    <span style={{
                      background: 'rgba(255,255,255,0.3)',
                      borderRadius: ABLE_COLORS.radiusSmall,
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}>
                      {selectedCount}
                    </span>
                  )}
                </button>
                
                {isExpanded && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 'auto',
                    marginTop: '8px',
                    padding: '12px',
                    background: ABLE_COLORS.neutral,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    boxShadow: ABLE_COLORS.cardShadowHover,
                    border: `1px solid ${ABLE_COLORS.border}`,
                    zIndex: 100,
                    minWidth: '180px',
                    maxWidth: 'calc(100vw - 40px)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {key === 'priority' && (
                        <button
                          onClick={() => setShowStarredOnly(!showStarredOnly)}
                          style={{
                            padding: '8px 12px',
                            background: showStarredOnly ? ABLE_COLORS.warm : ABLE_COLORS.neutral,
                            color: showStarredOnly ? 'white' : ABLE_COLORS.textSecondary,
                            border: `2px solid ${showStarredOnly ? ABLE_COLORS.warm : ABLE_COLORS.border}`,
                            borderRadius: ABLE_COLORS.radiusSmall,
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <Star size={12} fill={showStarredOnly ? 'white' : 'none'} />
                          Starred Only
                        </button>
                      )}
                      
                      {(key === 'priority' ? CONTEXT_OPTIONS.priority :
                        key === 'area' ? CONTEXT_OPTIONS.area :
                        key === 'project' ? contextOptions.project :
                        key === 'release' ? contextOptions.release :
                        contextOptions.workflow
                      ).map((option) => {
                        const isSelected = capacity[key].includes(option);

                        return (
                          <button
                            key={option}
                            onClick={() => toggleCapacity(key, option)}
                            style={{
                              padding: '8px 12px',
                              background: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                              color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                              border: `2px solid ${isSelected ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                              borderRadius: ABLE_COLORS.radiusSmall,
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              transition: 'all 0.15s ease',
                              textAlign: 'left'
                            }}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
